import Dialog, { type DialogProps } from '@mui/material/Dialog';
import { useEffect, type JSX, type ReactNode } from 'react';

import { useModalStack } from '../store/useModalStack';

export interface AppDialogProps extends Omit<DialogProps, 'onClose' | 'children'> {
  /** Unique id used by the modal stack — required for back-button + escape handling. */
  id: string;
  open: boolean;
  onClose: () => void;
  /** Allow Escape to close. Default `true`. */
  closeOnEscape?: boolean;
  children: ReactNode;
}

/**
 * Project-wide Dialog wrapper.
 *
 * - **Backdrop click does NOT close** (consistent UX across the app).
 * - Pushes a history entry on open so Android/iOS hardware Back closes the top dialog
 *   instead of navigating away. Multiple stacked dialogs unwind LIFO.
 * - Registers in the global modal stack ({@see useModalStack}) so non-Dialog UI
 *   (toasts, drawers) can introspect / dismiss.
 */
export function AppDialog({ id, open, onClose, closeOnEscape = true, children, ...rest }: AppDialogProps): JSX.Element {
  const push = useModalStack((s) => s.push);
  const pop = useModalStack((s) => s.pop);

  useEffect(() => {
    if (!open) return;
    push({ id, onClose });
    // Mark this history entry as belonging to a dialog so the global popstate listener
    // (in App.tsx) can decide to close instead of navigate.
    try {
      history.pushState({ chatModal: id }, '');
    } catch {
      // history API may be unavailable in some embedded contexts — silent fallback
    }
    return () => {
      pop(id);
    };
  }, [open, id, onClose, push, pop]);

  const handleClose: NonNullable<DialogProps['onClose']> = (_event, reason) => {
    if (reason === 'backdropClick') return;
    if (reason === 'escapeKeyDown' && !closeOnEscape) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} {...rest}>
      {children}
    </Dialog>
  );
}
